<template>
   <div class="header" key="card">
     <!-- Top row: Logo and Sign out button -->
     <div class="header-top-row">
       <img src="../assets/swfitLoan.svg" alt="Swift" class="header-img" />

       <!-- Page title block: every routed view gets the same command-bar treatment via its
            route meta (see router/index.js), so this doesn't need per-view markup. -->
       <div class="header-title-block">
         <!-- LoanFlow is the top-level page, so there is nothing to go back to from it. -->
         <router-link v-if="$route.name !== 'loantasks'" :to="`/LoanTasks/?id=${loanId}`" class="header-back-btn" title="LoanFlow">
           <i class="fa-solid fa-chevron-left"></i>
         </router-link>
         <!-- LoanFlow: same blue tile every other page header has (theirs is the back button), as a
              plain icon since there is nowhere to go back to. Matches swiftApp / connectGS3.0. -->
         <span v-else class="header-back-btn header-page-tile" aria-hidden="true">
           <i class="fa-solid fa-list-check"></i>
         </span>
         <div class="header-title-text">
           <h1>{{ pageTitle }}</h1>
           <p v-if="pageSubtitle">{{ pageSubtitle }}</p>
         </div>
       </div>

       <div class="signout-btn-box">
         <div class="header-actions-row">
           <!-- File Contacts cluster: stacked initials + count, click opens the full list.
                Contacts come from the store (see main.js loanContacts), populated by whichever
                view has loaded the loan's contact info. -->
           <div v-if="contacts.length" class="gs-contacts" @click.stop="contactsOpen = !contactsOpen">
             <span class="gs-contacts__label"><i class="fa-duotone fa-address-card"></i> File Contacts</span>
             <span class="gs-contacts__stack">
               <template v-for="(c, i) in contacts.slice(0, 3)" :key="'av' + i">
                 <!-- Internal staff show their Azure profile photo (same source the old
                      LoanLaunch "Your Team" card used); everyone else keeps initials. -->
                 <img v-if="photoOf(c)"
                      class="gs-avatar gs-avatar--photo"
                      :src="photoOf(c)"
                      :alt="c.name"
                      :title="c.role + ': ' + c.name"
                      @error="onAvatarError(c)" />
                 <span v-else
                       :class="['gs-avatar', 'gs-avatar--t' + (i % 5)]"
                       :title="c.role + ': ' + c.name">{{ initialsOf(c.name) }}</span>
               </template>
             </span>
             <span class="gs-contacts__count">
               {{ contacts.length }} contacts <i class="fa-solid fa-chevron-down"></i>
             </span>

             <div v-if="contactsOpen" class="gs-contacts__panel" @click.stop>
               <div v-for="(c, i) in contacts" :key="'c' + i" class="gs-contact-row">
                 <img v-if="photoOf(c)"
                      class="gs-avatar gs-avatar--photo"
                      :src="photoOf(c)"
                      :alt="c.name"
                      @error="onAvatarError(c)" />
                 <span v-else :class="['gs-avatar', 'gs-avatar--t' + (i % 5)]">{{ initialsOf(c.name) }}</span>
                 <span class="gs-contact-row__body">
                   <span class="gs-contact-row__role">{{ c.role }}</span>
                   <div class="gs-contact-row__name">{{ c.name }}</div>
                   <div v-if="c.email" class="gs-contact-row__meta">{{ c.email }}</div>
                   <div v-if="c.phone" class="gs-contact-row__meta">{{ c.phone }}</div>
                 </span>
                 <span v-if="c.email" class="gs-contact-row__mail" title="Email" @click="openEmail(c.email)">
                   <i class="fa-solid fa-envelope"></i>
                 </span>
               </div>
             </div>
           </div>

           <span class="gs-header-sep"></span>

           <k-button class="encompass-btn" @click="goToEncompass" :fill-mode="'flat'">
             <i class="fa-duotone fa-building-columns"></i> Open Encompass
           </k-button>
           <div class="notification-icon-container" @click="navigateToAlerts" :title="`${alertCount} unacknowledged alerts`">
             <span class="k-icon k-i-notification"></span>
             <span v-if="alertCount > 0" class="alert-badge">{{ alertCount }}</span>
           </div>
           <!-- File / Sales notes — lives in the header on every page now (it used to be a
                floating button pinned to the bottom-right of the viewport). -->
           <floating-file-notes-button />
           <!-- The signed-in user. This used to be a generic fa-user glyph wired to an empty
                deepLogout() -- it never showed who you actually were. Now it is the real
                account: the Azure headshot when there is one, initials from the MSAL account
                name when there isn't. -->
           <!-- While viewing as someone this is THEIR name and photo. When there is anyone to view as,
                the avatar opens swiftMarketing's "View as" menu. -->
           <div class="header-user" @click.stop>
             <div :class="['header-avatar', { 'header-avatar--menu': hasUserMenu, 'header-avatar--imp': impersonating }]"
                  :title="currentUserName || 'Account'"
                  @click="hasUserMenu && (userMenuOpen = !userMenuOpen)">
               <img v-if="currentUserPhoto && !avatarBroken"
                    class="header-avatar__img"
                    :src="currentUserPhoto"
                    :alt="currentUserName"
                    @error="avatarBroken = true" />
               <span v-else-if="currentUserName" class="header-avatar__initials">{{ initialsOf(currentUserName) }}</span>
               <i v-else class="fa-solid fa-user"></i>
             </div>

             <div v-if="userMenuOpen" class="header-user-menu">
               <div class="header-user-menu__head">
                 <strong>{{ currentUserName || 'Account' }}</strong>
                 <small v-if="impersonating" class="header-user-menu__imp"><i class="fa-solid fa-eye"></i> Viewing as<template v-if="impersonatingLabel"> · {{ impersonatingLabel }}</template></small>
                 <small v-else-if="currentUserEmail">{{ currentUserEmail }}</small>
               </div>
               <div v-if="showImpersonate" class="header-user-menu__item" @click="openImpersonate">
                 <i class="fa-light fa-user-secret"></i> {{ impersonating ? 'View as someone else…' : 'View as…' }}
               </div>
               <div v-if="impersonating" class="header-user-menu__item header-user-menu__item--danger" @click="stopImpersonate">
                 <i class="fa-light fa-eye-slash"></i> Stop viewing as {{ currentUserName || 'them' }}
               </div>
             </div>
           </div>
         </div>
       </div>
     </div>
     
     <!-- Second row: Navigation tabs. These same links now live in SideNav.vue on desktop;
          this row is kept only as the mobile fallback for when the sidebar hides itself
          below 630px (see .header-nav-row CSS below), so it stays byte-for-byte the same
          list SideNav.vue's navConfig.js carries. -->
     <div class="header-nav-row">
       <router-link :to="`/LoanTasks/?id=${loanId}`" class="router-link">LoanFlow</router-link>
       <router-link :to="`/ConsumerPortal/?id=${loanId}`" :class="['router-link']">LoanLaunch</router-link>
       <router-link :to="`/FileManager/?id=${loanId}`" class="router-link">File Manager</router-link>
       <router-link :to="`/Documents/?id=${loanId}`" class="router-link">Documents</router-link>
       <router-link :to="`/Prequal/?id=${loanId}`" class="router-link">Prequal</router-link>
       <router-link :to="`/Credit/?id=${loanId}`" class="router-link">Credit</router-link>
       <router-link :to="`/PricingEngine/?id=${loanId}`" class="router-link">Pricing</router-link>
       <router-link :to="`/?id=${loanId}`" class="router-link">Conditions</router-link>
       <router-link :to="`/PostClose/?id=${loanId}`" :class="['router-link']">Post Close</router-link>
       <router-link :to="`/Duplicate/?id=${loanId}`" class="router-link">Duplicate</router-link>
       <router-link :to="`/PurchaseAgreement/?id=${loanId}`" class="router-link">Purchase Agreement</router-link>
       <router-link v-if="hasClosingRole" :to="`/Closing/?id=${loanId}`" class="router-link">Closing</router-link>
       <router-link :to="`/SwiftData/?id=${loanId}`" class="router-link">SwiftData</router-link>
       <router-link :to="`/SwiftAlerts/?id=${loanId}`" class="router-link">Alerts</router-link>
       <!--<router-link :to="`/ScenarioManager/?id=${loanId}`" class="router-link">Scenario Manager</router-link>-->
     </div>
   </div>

   <!-- "View as…": swiftMarketing's picker (components/impersonation/ImpersonationDialog.vue). -->
   <impersonation-dialog v-if="showImpersonate" v-model:visible="impersonatePickerOpen" />

</template>

<script>
  import { Components } from '@formschema/native';
  import apiService from '../api/api.service.js';
  import auth from '../authConfig.js';
  import { Button } from '@progress/kendo-vue-buttons';
        import FloatingFileNotesButton from './FloatingFileNotesButton.vue'
        import bus from '@/helpers/appBus.js'
        import ImpersonationDialog from './impersonation/ImpersonationDialog.vue'
        import { canImpersonate, getActAs, refreshImpersonation, stopImpersonation } from '@/helpers/impersonation.js'

  // Encompass field ids behind the header's File Contacts cluster.
  const CONTACT_FIELD_IDS = [
    '317', '1406', '1407',            // loan officer
    '362', '1408', '1409',            // loan processor
    '4000', '4002', '1240', '1490', '66',   // borrower
    '4004', '4006', '1268', '1480',   // coborrower
    'CD5.X55', 'CD5.X65', 'CD5.X66',  // title
    'VEND.X139', 'VEND.X140', 'VEND.X141', // buyer agent
    'VEND.X150', 'VEND.X151', 'VEND.X152'  // seller agent
  ];

  // Internal (Azure) roles whose profile photo we can resolve for the File Contacts
  // cluster. Keys are the contactType GetLoanTeamMembers returns; values are the
  // role labels loadFileContacts() builds its rows with. External contacts
  // (borrower, title, agents) have no Azure account and keep their initials chip.
  const AZURE_ROLE_TO_CONTACT_ROLE = {
    LOAN_OFFICER: 'Loan Officer',
    LOAN_PROCESSOR: 'Loan Processor'
  };

  // The rest of the file's people. Same three lists teamDataDialog.vue asks for, so the
  // cluster and that dialog can't disagree about who is on a loan.
  const LOAN_CONTACT_ROLE_LABELS = {
    LOAN_OFFICER: 'Loan Officer',
    LOAN_PROCESSOR: 'Loan Processor',
    SELLERS_AGENT: 'Seller Agent',
    BUYERS_AGENT: 'Buyer Agent',
    TITLE_COMPANY: 'Title',
    ESCROW_COMPANY: 'Escrow'
  };

  const MILESTONE_FREE_ROLES = ['Loan Partner', 'Loan Officer Asst.', 'Jr Processor', 'Account Manager'];

  // The order the cluster reads in: the people on the other side of the table first -- the
  // borrowers, then the agents, then title/escrow -- and the Gold Star team after them. An LO
  // opening this is looking for someone else's number; their own colleagues they already know.
  //
  // Roles are normalised before matching -- lower-cased, underscores and hyphens to spaces,
  // apostrophes dropped -- so "BUYERS_AGENT", "Buyer Agent" and "Buyer's Agent" all land in the
  // same bucket. Each list is in DISPLAY order, and the LONGEST matching key wins: that is what
  // keeps "Coborrower" from being filed as a "borrower" and "Loan Officer Asst." from being
  // filed as a "loan officer". Ordering the keys by specificity instead would have forced the
  // assistant to display above the officer.
  const normalizeRole = role => String(role || '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/[''`]/g, '')
    // "Buyers Agent" / "Buyer's Agent" / "Buyer Agent" are the same role spelled three ways in
    // Encompass. Folding the plural here means one key covers all of them, instead of near-
    // identical spellings sorting into adjacent-but-different buckets.
    .replace(/\b(buyer|seller)s\b/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();

  const EXTERNAL_ROLE_ORDER = [
    'borrower',
    'coborrower', 'co borrower',
    'buyer agent',
    'seller agent',
    'agent', 'realtor',
    'title',
    'escrow',
    'closing',
    'apprais',
    'insur',
    'builder',
    'attorney'
  ];

  const INTERNAL_ROLE_ORDER = [
    'loan officer',
    'loan officer asst',
    'loan partner',
    'loan processor',
    'jr processor', 'junior processor',
    'account manager',
    'underwriter',
    'closer',
    'funder'
  ];

  // Internal or not is decided by the ROLE — the same label the user is reading — and the
  // external table is consulted FIRST so a third party can never be misfiled as staff.
  //
  // This replaced a "whichever endpoint returned it" rule, which looked more rigorous and was
  // wrong: GetLoanTeamMembers is asked for `loanContacts` too (see LOAN_CONTACT_ROLE_LABELS),
  // so it returns TITLE_COMPANY, ESCROW_COMPANY and both agents alongside the Azure staff.
  // Flagging everything it answered with as internal sent the title company to the bottom of
  // the list, below the LO.
  //
  // An unrecognised role is external: an unknown name on a loan file is far more often a third
  // party than an employee, and putting a stranger among the staff reads worse than the reverse.
  function isInternalRole(role) {
    const r = normalizeRole(role);
    if (EXTERNAL_ROLE_ORDER.some(key => r.includes(key))) return false;
    return INTERNAL_ROLE_ORDER.some(key => r.includes(key));
  }

  export default {
    data() {
      return {
        azureId: null,
        userRoles: [],
        contactsOpen: false,
        // Keyed by contact email (or name) -- an <img> that failed to decode is
        // remembered here so the row falls back to initials instead of flickering.
        brokenPhotos: {},
        // The signed-in user, for the avatar at the end of the header row.
        currentUserName: '',
        currentUserEmail: '',
        currentUserPhoto: '',
        avatarBroken: false,
        // "View as" (helpers/impersonation.js)
        showImpersonate: false,
        impersonating: false,
        impersonatingLabel: '',
        userMenuOpen: false,
        impersonatePickerOpen: false,
      }
    },
 
    components:{
        'k-button': Button,
        'floating-file-notes-button': FloatingFileNotesButton,
        'impersonation-dialog': ImpersonationDialog
    },
    computed: {
        processingLink() {
            if (this.$route.query.swiftprocessing) {
                return true;
            }

            return false;
        },
        loanId() {
            return this.$route.query.id;
        },
        pageTitle() {
            return this.$route.meta?.title || 'Swift';
        },
        pageSubtitle() {
            return this.$route.meta?.subtitle || '';
        },
        contacts() {
            return this.$store.state.loanContacts || [];
        },
        hasUserMenu() {
            return this.showImpersonate || this.impersonating;
        },
        hasClosingRole() {
          return this.userRoles.includes("Closing");
        }

      , hasDevRole() {
        return this.userRoles.includes("Dev");
      }

      , alertCount() {
        return this.$store.state.unacknowledgedAlertCount || 0;
      }

      , isDevEnvironment() {
        const hostname = window.location.hostname;
        return hostname === 'localhost' || hostname === 'dev-swift.goldstarfinancial.com';
      }
    },
    async created() {
      const msalAppInstance = await auth.init();
      let tokenResponse = await auth.acquireTokenResponse();

      // "View as" works as in swiftMarketing: the server decides who may view as whom (GSDesignImpersonation)
      // and re-checks the person here on every load. A refused one comes back '' and the page runs as you.
      const actingId = await refreshImpersonation();
      const actAs = actingId ? getActAs() : null;
      this.impersonating = !!actAs;
      this.impersonatingLabel = actAs?.target_label || '';
      this.showImpersonate = canImpersonate();
      this.azureId = actingId || tokenResponse.uniqueId;

      // MSAL's account is the authoritative "who is signed in": name is the display name and
      // username is the UPN/email. authConfig.js also mirrors the name to localStorage, which
      // is the fallback if the account object isn't populated on a warm start. While viewing as
      // someone the avatar is theirs instead.
      this.currentUserName = actAs ? actAs.target_name || '' : tokenResponse?.account?.name || localStorage.getItem('user_name') || '';
      this.currentUserEmail = actAs ? actAs.email || '' : tokenResponse?.account?.username || '';
      this.loadCurrentUserPhoto(tokenResponse.accessToken);

      const response = await apiService.GetRoles(this.azureId, tokenResponse.accessToken);
      const data = response.data;
      if (data && data.issuccess) {
        this.userRoles = data.data;
        localStorage.setItem('user_roles', data.data);
      } else {
        console.error("No valid user role data received from API");
      }
      // SideNav.vue's Closing nav item is gated on this same role list, and SideNav can mount
      // before this async fetch resolves — this lets it re-check once roles actually land.
      bus.emit('user-roles-ready');
    },
    async mounted() {
      document.addEventListener('click', this.onDocumentClick);
      // Load alert count when component is mounted (route is ready)
      await this.loadAlertCount();
      await this.loadFileContacts();
    },
    beforeUnmount() {
      document.removeEventListener('click', this.onDocumentClick);
    },
    watch: {
      '$route.query.id'(newLoanId) {
        if (newLoanId) {
          this.loadAlertCount();
          this.loadFileContacts();
        }
      }
    },
    methods: {
        // The header is the one component on every route, so it owns loading the file's
        // contacts for the "File Contacts" cluster. Views no longer need to publish them.
        //
        // Three sources, merged, because no single one has everyone:
        //   1. Encompass fields  -- the seven roles the loan form itself carries.
        //   2. GetLoanTeamMembers -- the internal team, including the four milestone-free
        //      roles (Loan Partner, LO Asst., Jr Processor, Account Manager) that have no
        //      field IDs, and the Azure headshots.
        //   3. GetContactByLoanIdWithDetails -- whatever else Encompass holds against the
        //      file that neither of the above enumerates.
        // Deduped on email, since that is the only identifier all three agree on; a source
        // that fails is skipped rather than emptying the cluster.
        async loadFileContacts() {
          if (!this.loanId) return;

          try {
            const rows = await this.loadEncompassFieldContacts();
            await this.mergeTeamMemberContacts(rows);
            await this.mergeEncompassContactRecords(rows);

            if (!rows.length) return;
            this.$store.commit('updateLoanContacts', this.sortContactRows(rows));
          } catch (error) {
            console.error('[Header] Failed to load file contacts:', error);
          }
        },
        async loadEncompassFieldContacts() {
          const fields = await this.$store.dispatch('getLoanFields', CONTACT_FIELD_IDS);
          if (!fields || !fields.length) return [];

          const val = id => {
            const f = fields.find(x => x.fieldId === id);
            return f && f.value ? String(f.value).trim() : '';
          };
          const joinName = (first, last) => [val(first), val(last)].filter(Boolean).join(' ');

          // Display order comes from the role alone (see isInternalRole / sortContactRows);
          // no row carries a flag saying which side of the table it sits on.
          return [
            { role: 'Loan Officer',   name: val('317'),               email: val('1407'), phone: val('1406') },
            { role: 'Loan Processor', name: val('362'),               email: val('1409'), phone: val('1408') },
            { role: 'Borrower',       name: joinName('4000', '4002'), email: val('1240'), phone: val('1490') || val('66') },
            { role: 'Coborrower',     name: joinName('4004', '4006'), email: val('1268'), phone: val('1480') },
            { role: 'Title',          name: val('CD5.X55'),           email: val('CD5.X65'), phone: val('CD5.X66') },
            { role: 'Buyer Agent',    name: val('VEND.X139'),         email: val('VEND.X141'), phone: val('VEND.X140') },
            { role: 'Seller Agent',   name: val('VEND.X150'),         email: val('VEND.X152'), phone: val('VEND.X151') }
          ].filter(r => r.name && r.name !== 'N/A');
        },
        // Adds the internal team and, where a row already exists, fills in its photo.
        async mergeTeamMemberContacts(rows) {
          try {
            const tokenResponse = await auth.acquireTokenResponse();
            const request = {
              azureRoles: Object.keys(AZURE_ROLE_TO_CONTACT_ROLE),
              loanContacts: Object.keys(LOAN_CONTACT_ROLE_LABELS),
              milestoneFreeRoles: MILESTONE_FREE_ROLES
            };

            const response = await apiService.GetLoanTeamMembers(this.loanId, request, tokenResponse.accessToken);
            const members = response?.data?.data || [];

            members.forEach(member => {
              const name = String(member.name || member.contactName || '').trim();
              if (!name || name === 'N/A') return;

              const role = AZURE_ROLE_TO_CONTACT_ROLE[member.contactType]
                || LOAN_CONTACT_ROLE_LABELS[member.contactType]
                || member.contactType
                || 'Team';

              const row = this.findContactRow(rows, member.email, role);
              const photo = this.toImageSrc(member.base64Image);

              if (row) {
                if (photo && !row.photo) row.photo = photo;
                if (!row.phone) row.phone = member.phone || member.cell || '';
                if (!row.email) row.email = member.email || '';
              } else {
                rows.push({
                  role,
                  name,
                  email: member.email || '',
                  phone: member.phone || member.cell || '',
                  photo: photo || ''
                });
              }
            });
          } catch (error) {
            console.error('[Header] Failed to load team member contacts:', error);
          }
        },
        // Sweeps up anything Encompass holds that the two role lists above don't name.
        async mergeEncompassContactRecords(rows) {
          try {
            const tokenResponse = await auth.acquireTokenResponse();
            const response = await apiService.GetContactByLoanIdWithDetails(this.loanId, tokenResponse.accessToken);
            const records = response?.data?.data || [];

            records.forEach(record => {
              const name = String(
                record.name || record.contactName ||
                [record.firstName, record.lastName].filter(Boolean).join(' ')
              ).trim();
              if (!name || name === 'N/A') return;

              const role = record.role || record.contactType || record.type || 'Contact';
              if (this.findContactRow(rows, record.email, role)) return;

              rows.push({
                role: this.prettyRole(role),
                name,
                email: record.email || '',
                phone: record.phone || record.cell || record.mobile || ''
              });
            });
          } catch (error) {
            console.error('[Header] Failed to load Encompass contact records:', error);
          }
        },
        // External first (borrowers, agents, title/escrow, other third parties), then the
        // internal team. Within each half, the role order above; anything unrecognised falls to
        // the end of its own half rather than jumping the queue. A stable sort keeps two
        // borrowers in the order Encompass listed them.
        sortContactRows(rows) {
          const rank = row => {
            const role = normalizeRole(row.role);
            const internal = isInternalRole(row.role);
            const table = internal ? INTERNAL_ROLE_ORDER : EXTERNAL_ROLE_ORDER;
            let best = -1;
            let bestLen = 0;
            table.forEach((key, i) => {
              if (role.includes(key) && key.length > bestLen) {
                best = i;
                bestLen = key.length;
              }
            });
            const within = best === -1 ? table.length : best;
            return (internal ? 1000 : 0) + within;
          };
          return rows
            .map((row, i) => ({ row, i }))
            .sort((a, b) => (rank(a.row) - rank(b.row)) || (a.i - b.i))
            .map(x => x.row);
        },
        // Email is the hard identifier; role is the fallback for a record that has no email.
        findContactRow(rows, email, role) {
          const norm = v => String(v || '').trim().toLowerCase();
          if (norm(email)) {
            const byEmail = rows.find(r => norm(r.email) === norm(email));
            if (byEmail) return byEmail;
          }
          return rows.find(r => norm(r.role) === norm(role)) || null;
        },
        // TITLE_COMPANY -> Title Company, so a raw contactType never reaches the UI.
        prettyRole(role) {
          return String(role || '')
            .replace(/[_-]+/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, c => c.toUpperCase())
            .trim() || 'Contact';
        },
        // The signed-in user's own headshot, by email. Same per-user lookup the LoanLaunch
        // team card uses, so the face here is the one that shows up in File Contacts too.
        // Failure is silent -- the avatar just falls back to initials.
        async loadCurrentUserPhoto(accessToken) {
          if (!this.currentUserEmail) return;

          try {
            const response = await apiService.GetLOProfilePhoto(this.currentUserEmail, accessToken);
            const src = this.toImageSrc(response?.data?.data);
            if (src) {
              this.currentUserPhoto = src;
              this.avatarBroken = false;
            }
          } catch (error) {
            console.error('[Header] Failed to load the signed-in user photo:', error);
          }
        },
        // The API has returned both a bare base64 payload and a full data URI over
        // time; normalize so the <img> src is valid either way.
        toImageSrc(value) {
          const raw = String(value || '').trim();
          if (!raw) return '';
          return raw.startsWith('data:') ? raw : `data:image/jpeg;base64,${raw}`;
        },
        photoOf(contact) {
          if (!contact || !contact.photo) return '';
          return this.brokenPhotos[contact.email || contact.name] ? '' : contact.photo;
        },
        onAvatarError(contact) {
          if (!contact) return;
          this.brokenPhotos = { ...this.brokenPhotos, [contact.email || contact.name]: true };
        },
        async loadAlertCount() {
          if (!this.loanId) {
            console.log('[Header] No loanId available yet, skipping alert count load');
            return;
          }

          try {
            console.log(`[Header] Loading alert count for loan ${this.loanId}`);
            const tokenResponse = await auth.acquireTokenResponse();
            const response = await apiService.GetLoanAlerts(this.loanId, tokenResponse.accessToken);

            if (response.data && response.data.isSuccess && response.data.data) {
              const alerts = response.data.data;
              const unacknowledgedCount = alerts.filter(a => !a.acknowledged && !a.dismissed).length;
              this.$store.commit('updateAlertCount', unacknowledgedCount);
              console.log(`[Header] Updated alert count: ${unacknowledgedCount}`);
            }
          } catch (error) {
            console.error('[Header] Failed to load alert count:', error);
          }
        },
        initialsOf(name) {
          const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
          if (!parts.length) return '?';
          if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
          return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        },
        openEmail(email) {
          if (email) window.open(`mailto:${email}`, '_blank');
        },
        onDocumentClick() {
          this.contactsOpen = false;
          this.userMenuOpen = false;
        },
        openImpersonate() {
          this.userMenuOpen = false;
          this.impersonatePickerOpen = true;
        },
        async stopImpersonate() {
          this.userMenuOpen = false;
          await stopImpersonation(); // reloads this page as the signed-in user
        },
        navigateToAlerts() {
          if (this.loanId) {
            this.$router.push(`/SwiftAlerts/?id=${this.loanId}`);
          }
        },
        goToEncompass() {
          if (this.loanId) {
            const url = `https://www.encompassloconnect.com/pipeline/${this.loanId}`;
            const width = screen.width * 0.8;
            const height = screen.height * 0.8;
            const left = (screen.width - width) / 2;
            const top = (screen.height - height) / 2;
            window.open(url, '_blank', `width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`);
          }
        }
    }
  }
</script>

<style>


.ai-btn {
    background-color: var(--con-primary-ltr)!important;
    font-weight: 600;
    font-size: 1.2rem;
}
  .header {
    border-bottom: 2px solid var(--accent);
    padding: 20px 10px 10px;
  }

    .header-top-row{
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
        gap: 16px;
    }

    .header-title-block {
        display: flex;
        align-items: center;
        gap: 14px;
        flex: 1 1 auto;
        min-width: 0;
    }

    .header-back-btn {
        flex: 0 0 auto;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: var(--background-dk);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: var(--primary);
        text-decoration: none;
        font-size: 1.3rem;
        transition: background-color 0.2s ease;
    }

    .header-back-btn:hover {
        background: var(--accent-ltr);
    }

    .header-title-text {
        min-width: 0;
    }

    .header-title-text h1 {
        margin: 0;
        font-size: 2rem;
        font-weight: 700;
        color: var(--primary);
        line-height: 1.2;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .header-title-text p {
        margin: 2px 0 0;
        font-size: 1.2rem;
        font-weight: 500;
        color: var(--text-secondary-on-background, #8f8f8f);
        white-space: nowrap;
    }

    @media only screen and (max-width: 630px) {
        .header-title-block {
            display: none;
        }
    }

    /* SideNav.vue owns these links on desktop now (see its navConfig.js) — this row, and the
       logo above it, only reappear as the mobile fallback once the sidebar hides itself
       below 630px (same breakpoint as SideNav's own @media rule). */
    .header-nav-row{
        display: none;
        align-items: flex-end;
    }

    .header-top-row .header-img {
        display: none;
    }

    @media only screen and (max-width: 630px) {
        .header-nav-row {
            display: flex;
        }

        .header-top-row .header-img {
            display: block;
        }
    }

    /* The logo only. This used to be `.header img`, which also hit the File Contacts photos
       and, being scoped, beat img.gs-avatar's 26px -- 26 wide x 40 tall, an oval. */
    .header .header-img {
        height: 40px;
    }

    .header-nav-row .router-link {
        text-decoration: none;
        margin-bottom: 3px;
        color: var(--primary);
        font-size: 1.8rem;
        /*min-width: 90px;*/
        padding-left: 35px;
        text-align:center;
    }

    .header-nav-row .router-link:first-child {
        margin-left: 0;
    }

    .header-nav-row .router-link.disabled {
        pointer-events: none;
    }

    .header-nav-row .router-link:hover {
        color: var(--text-secondary-on-background);
    }

    .header-nav-row .router-link.router-link-active {
        color: var(--accent);
        font-weight: 600;
    }

    .header-nav-row .router-link.fade-on-hover {
        opacity: 0;
        transition: opacity 0.3s ease;
    }

    .header-nav-row .router-link.fade-on-hover:hover {
        opacity: 1;
    }

    .signout-btn-box{
        display: flex;
        align-items: center;
        gap: 10px;
        position: relative;
        flex: 0 0 auto;
    }

    .signout-btn {
        display: block;
        font-weight: 500;
        text-align: center;
        white-space: nowrap;
        vertical-align: middle;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        background: none;
        padding: .7rem 1rem;
        margin-left: auto;
        border: none;
        opacity: 0;
        transition: opacity 0.3s ease;
    }

    .signout-btn:hover {
        opacity: 1;
        cursor: pointer;
        background: var(--accent-ltr);
        border-radius: 1rem;
    }

    .header-actions-row {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .header-user {
        position: relative;
        flex: 0 0 auto;
    }

    .header-avatar--menu {
        cursor: pointer;
    }

    .header-avatar--imp {
        box-shadow: 0 0 0 2px #fff, 0 0 0 4px #b45309;
    }

    .header-user-menu {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        min-width: 240px;
        background: #fff;
        border-radius: 12px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
        overflow: hidden;
        z-index: 1000;
        font-size: 1.3rem;
    }

    .header-user-menu__head {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 12px 14px;
        border-bottom: 1px solid #e5e5e5;
        color: var(--primary);
    }

    .header-user-menu__head small {
        color: #666;
    }

    .header-user-menu__imp {
        color: #b45309 !important;
        font-weight: 600;
    }

    .header-user-menu__item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 14px;
        cursor: pointer;
        color: var(--primary);
    }

    .header-user-menu__item i {
        width: 18px;
        text-align: center;
    }

    .header-user-menu__item:hover {
        background: #f4f6f9;
    }

    .header-user-menu__item--danger {
        color: #c62828;
        font-weight: 600;
    }

    .header-avatar {
        flex: 0 0 auto;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: var(--primary);
        color: #fff;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 1.4rem;
    }

    /* Encompass Button Styles */
    .encompass-btn {
        background-color: rgba(0, 0, 0, 0.05) !important;
        border: none !important;
        border-radius: 8px !important;
        color: var(--primary) !important;
        font-weight: 500;
        padding: 8px 14px !important;
        transition: all 0.3s ease !important;
    }

    .encompass-btn:hover {
        background-color: var(--accent-ltr) !important;
    }

    .encompass-btn i {
        margin-right: 6px;
    }

    /* Notification Icon Styles */
    .notification-icon-container {
        position: relative;
        cursor: pointer;
        padding: 8px 12px;
        border-radius: 8px;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .notification-icon-container:hover {
        background-color: var(--accent-ltr);
    }

    .notification-icon-container .k-icon {
        font-size: 2.4rem;
        color: var(--primary);
    }

    .notification-icon-container .alert-badge {
        position: absolute;
        top: 2px;
        right: 4px;
        background: #dc2626;
        color: white;
        border-radius: 50%;
        min-width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 700;
        padding: 2px 4px;
        animation: pulse-badge 2s ease-in-out infinite;
    }

    @keyframes pulse-badge {
        0%, 100% {
            transform: scale(1);
        }
        50% {
            transform: scale(1.1);
        }
    }

    @media only screen and (max-width: 1200px) and (orientation: portrait) {
        .header-nav-row .router-link {
            margin-right: 15px;
            font-size: 1.5rem;
            /*min-width: 90px;*/
        }
    }

    @media only screen and (max-width: 700px) and (orientation: portrait) {
        .header-nav-row .router-link {
            display: none;
        }

        .header-actions-row {
          display: none;
        }
    }

</style>
